import React, { useState } from 'react';

function Help() {
  const [activeSection, setActiveSection] = useState('getting-started');
  const [searchQuery, setSearchQuery] = useState('');

  const sections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: '🚀',
      content: [
        {
          title: 'Welcome to PM Training Hub',
          text: 'PM Training Hub is your centralized platform for project management training, certification preparation, and professional development. This guide will help you navigate the system and make the most of its features.'
        },
        {
          title: 'First Steps',
          text: 'After logging in, you\'ll see your Dashboard which shows your assigned modules, progress, and upcoming deadlines. Start by exploring the Module Library to see available training content.'
        },
        {
          title: 'Navigation',
          text: 'Use the navigation bar at the top to access different sections. On mobile devices, tap the hamburger menu (☰) to open the navigation drawer.'
        }
      ]
    },
    {
      id: 'modules',
      title: 'Training Modules',
      icon: '📖',
      content: [
        {
          title: 'Module Library',
          text: 'The Module Library contains all available training modules organized by category. Each module includes learning objectives, content, quizzes, and resources.'
        },
        {
          title: 'Starting a Module',
          text: 'Click on any module to view its details. Once assigned, you can track your progress through the module content, complete quizzes, and mark sections as complete.'
        },
        {
          title: 'Module Progress',
          text: 'Your progress is automatically tracked as you complete sections. The progress bar shows your completion percentage, and you can see estimated time remaining.'
        },
        {
          title: 'Quizzes & Assessments',
          text: 'Most modules include quizzes to test your understanding. You can retake quizzes to improve your scores. A passing score is typically 70% or higher.'
        }
      ]
    },
    {
      id: 'progress',
      title: 'Tracking Progress',
      icon: '📈',
      content: [
        {
          title: 'Progress Dashboard',
          text: 'The Progress page shows all your assigned modules with their current status, due dates, and completion percentages. Use filters to view specific categories or statuses.'
        },
        {
          title: 'Status Indicators',
          text: '• Not Started - Module assigned but not begun\n• In Progress - Currently working on module\n• Completed - All requirements met\n• On Hold - Temporarily paused'
        },
        {
          title: 'Due Dates',
          text: 'Modules with approaching due dates are highlighted. Red indicates overdue, yellow indicates due soon (within 14 days).'
        }
      ]
    },
    {
      id: 'documents',
      title: 'Documents',
      icon: '📚',
      content: [
        {
          title: 'Document Library',
          text: 'Access supplementary materials, templates, guides, and reference documents. Documents are organized by category for easy browsing.'
        },
        {
          title: 'Downloading',
          text: 'Click the download button next to any document to save it to your device. Some documents may be view-only.'
        },
        {
          title: 'Uploading Documents',
          text: 'If you have permission, you can upload documents to share with your team. Supported formats include PDF, Word, Excel, and PowerPoint.'
        }
      ]
    },
    {
      id: 'study-groups',
      title: 'Study Groups',
      icon: '👥',
      content: [
        {
          title: 'About Study Groups',
          text: 'Study Groups allow you to collaborate with other PMs preparing for the same certification. Share resources, discuss topics, and support each other\'s learning.'
        },
        {
          title: 'Joining a Group',
          text: 'Browse available study groups and request to join. Group leaders will approve your request. You can be a member of multiple groups.'
        },
        {
          title: 'Group Features',
          text: '• Discussion threads for each topic\n• Shared resource library\n• Group calendar for study sessions\n• Progress comparison with group members'
        }
      ]
    },
    {
      id: 'mentorships',
      title: 'Mentorships',
      icon: '🤝',
      content: [
        {
          title: 'Finding a Mentor',
          text: 'Experienced PMs can mentor those newer to the field. Browse available mentors and request a connection based on your learning goals.'
        },
        {
          title: 'Mentor-Mentee Relationship',
          text: 'Once connected, you can schedule sessions, share progress, and get guidance. Communication happens through the platform\'s messaging system.'
        },
        {
          title: 'Becoming a Mentor',
          text: 'Certified PMs can volunteer to mentor others. Contact your manager to be added to the mentor pool.'
        }
      ]
    },
    {
      id: 'workload',
      title: 'PM Workload',
      icon: '📊',
      content: [
        {
          title: 'Workload Dashboard',
          text: 'View the current workload distribution across all PMs. This helps managers make informed assignment decisions.'
        },
        {
          title: 'Workload Metrics',
          text: '• Active Projects - Current project assignments\n• Training Load - Hours of training assigned\n• Availability - PM capacity for new work'
        },
        {
          title: 'Workload Settings',
          text: 'Administrators can configure workload calculation parameters, including training project weights and capacity limits.'
        }
      ]
    },
    {
      id: 'admin',
      title: 'Administration',
      icon: '🔧',
      content: [
        {
          title: 'Admin Panel',
          text: 'Administrators can manage users, modules, and system settings. Access requires admin privileges.'
        },
        {
          title: 'Module Assignments',
          text: 'Assign training modules to individual PMs or groups. Set due dates and track completion across your team.'
        },
        {
          title: 'Manager Dashboard',
          text: 'Get an overview of your team\'s training progress, certification status, and upcoming milestones.'
        },
        {
          title: 'User Management',
          text: 'Add new users, update roles, reset passwords, and manage access permissions.'
        }
      ]
    },
    {
      id: 'keyboard',
      title: 'Keyboard Shortcuts',
      icon: '⌨️',
      content: [
        {
          title: 'Navigation Shortcuts',
          text: '• Alt + D - Go to Dashboard\n• Alt + M - Go to Modules\n• Alt + P - Go to Progress\n• Alt + H - Open Help'
        },
        {
          title: 'Module Viewer Shortcuts',
          text: '• Arrow Left/Right - Previous/Next section\n• Space - Mark section complete\n• Q - Open quiz'
        }
      ]
    },
    {
      id: 'faq',
      title: 'FAQ',
      icon: '❓',
      content: [
        {
          title: 'How do I reset my password?',
          text: 'Click "Forgot Password" on the login page and enter your email. You\'ll receive a link to reset your password.'
        },
        {
          title: 'Can I access training on mobile?',
          text: 'Yes! PM Training Hub is fully responsive and works on phones and tablets. Use the mobile menu to navigate.'
        },
        {
          title: 'How are quiz scores calculated?',
          text: 'Your score is the percentage of correct answers. Most quizzes require 70% to pass. You can retake quizzes to improve your score.'
        },
        {
          title: 'What if I need more time for a module?',
          text: 'Contact your manager to request a due date extension. They can adjust deadlines through the Assignment page.'
        },
        {
          title: 'How do I report a bug or issue?',
          text: 'Contact your system administrator or use the feedback form (if available). Include details about what you were doing when the issue occurred.'
        }
      ]
    },
    {
      id: 'contact',
      title: 'Contact & Support',
      icon: '📧',
      content: [
        {
          title: 'Technical Support',
          text: 'For technical issues with the platform, contact your IT administrator or system admin.'
        },
        {
          title: 'Training Questions',
          text: 'For questions about training content or assignments, contact your direct manager or training coordinator.'
        },
        {
          title: 'Feedback',
          text: 'We welcome your feedback to improve the platform. Share suggestions with your manager or administrator.'
        }
      ]
    }
  ];

  // Filter sections based on search query
  const filteredSections = searchQuery
    ? sections.filter(section =>
        section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        section.content.some(item =>
          item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.text.toLowerCase().includes(searchQuery.toLowerCase())
        )
      )
    : sections;

  const currentSection = sections.find(s => s.id === activeSection) || sections[0];

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center">
          <span className="mr-3">📖</span>
          Help & Documentation
        </h1>
        <p className="text-gray-600 mt-2">
          Learn how to use PM Training Hub effectively
        </p>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search help topics..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            🔍
          </span>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar Navigation */}
        <div className="lg:w-64 flex-shrink-0">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-3 bg-gray-50 border-b border-gray-200">
              <h2 className="font-semibold text-gray-700">Topics</h2>
            </div>
            <nav className="p-2">
              {filteredSections.map(section => (
                <button
                  key={section.id}
                  onClick={() => {
                    setActiveSection(section.id);
                    setSearchQuery('');
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center ${
                    activeSection === section.id
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span className="mr-2">{section.icon}</span>
                  {section.title}
                </button>
              ))}
            </nav>
          </div>

          {/* Quick Links */}
          <div className="mt-4 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-700 mb-3">Quick Links</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="/modules" className="text-blue-600 hover:underline flex items-center">
                  <span className="mr-2">→</span>Module Library
                </a>
              </li>
              <li>
                <a href="/progress" className="text-blue-600 hover:underline flex items-center">
                  <span className="mr-2">→</span>My Progress
                </a>
              </li>
              <li>
                <a href="/documents" className="text-blue-600 hover:underline flex items-center">
                  <span className="mr-2">→</span>Documents
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Section Header */}
            <div className="p-4 md:p-6 border-b border-gray-200">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center">
                <span className="mr-3">{currentSection.icon}</span>
                {currentSection.title}
              </h2>
            </div>

            {/* Section Content */}
            <div className="p-4 md:p-6">
              <div className="space-y-6">
                {currentSection.content.map((item, index) => (
                  <div key={index} className="border-l-4 border-blue-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 whitespace-pre-line">
                      {item.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Footer */}
            <div className="p-4 md:p-6 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
              {sections.findIndex(s => s.id === activeSection) > 0 ? (
                <button
                  onClick={() => {
                    const idx = sections.findIndex(s => s.id === activeSection);
                    setActiveSection(sections[idx - 1].id);
                  }}
                  className="text-blue-600 hover:text-blue-800 font-medium flex items-center"
                >
                  <span className="mr-2">←</span>
                  Previous: {sections[sections.findIndex(s => s.id === activeSection) - 1]?.title}
                </button>
              ) : <div />}

              {sections.findIndex(s => s.id === activeSection) < sections.length - 1 && (
                <button
                  onClick={() => {
                    const idx = sections.findIndex(s => s.id === activeSection);
                    setActiveSection(sections[idx + 1].id);
                  }}
                  className="text-blue-600 hover:text-blue-800 font-medium flex items-center"
                >
                  Next: {sections[sections.findIndex(s => s.id === activeSection) + 1]?.title}
                  <span className="ml-2">→</span>
                </button>
              )}
            </div>
          </div>

          {/* Still Need Help? */}
          <div className="mt-6 bg-blue-50 rounded-lg border border-blue-200 p-6 text-center">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">
              Still need help?
            </h3>
            <p className="text-blue-700 mb-4">
              If you can't find what you're looking for, contact your manager or system administrator.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={() => setActiveSection('contact')}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Contact Support
              </button>
              <button
                onClick={() => setActiveSection('faq')}
                className="px-4 py-2 bg-white text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50 transition-colors"
              >
                View FAQ
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Help;
